import React, { useState } from "react";

import Markdown from "markdown-to-jsx";
import { markdownStyleOverrides } from "../../../../../lib/utils";
import dislike from "../../../../../assets/imgs/dislike_active.svg";
import like from "../../../../../assets/imgs/like.svg";
import likeInactive from "../../../../../assets/imgs/like-inactive.svg";
import dislikeInactive from "../../../../../assets/imgs/dislike.svg";
import followUpQuestions from "../../../../../assets/imgs/follow_up_questions.svg";
import followUp from "../../../../../assets/imgs/follow_up_icon.svg";
import "../RenderChatBotResponse.css";
import { hapticsImpactLight } from "../../Services";
import { useSocket } from "../../../../../Context/SocketContext/SocketProvider";
import ClickableBold from "../../ChatbotRenderComponet/ClickableBold";
import InteractiveTable from "../../ChatbotRenderComponet/InteractiveTable";
import ChartRender from "../../ChatbotRenderComponet/ChartRender";

interface ChatBotHistoryProps {
  userQuestion: string;
  botResponse: string;
  relatedQuestions: string[];
  liked?: boolean | null;
  threadId?: string | null | undefined;
  checkpointId?: string | null | undefined;
  onQuestionClick?: (question: string) => void;
  typingLoading?: boolean;
  followUpQuestionsLoading?: boolean;
  showSkeleton?: boolean;
  messageLoading?: boolean;
  modalDatathreadId?: string | null | undefined;
  chartData?: string | null | undefined;
}

const ChatBotHistory: React.FC<ChatBotHistoryProps> = ({
  userQuestion,
  botResponse,
  relatedQuestions,
  liked = false,
  threadId,
  checkpointId,
  onQuestionClick,
  typingLoading = false,
  followUpQuestionsLoading = false,
  showSkeleton = false,
  messageLoading = false,
  modalDatathreadId,
  chartData,
}) => {
  const [likeClicked, setLikeClicked] = useState(liked === true);
  const [dislikeClicked, setDislikeClicked] = useState(liked === false);
  const [likeAnimating, setLikeAnimating] = useState(false);
  const [dislikeAnimating, setDislikeAnimating] = useState(false);
  const { likeMessage } = useSocket();

  const handleLikeClick = async () => {
    // If already liked, don't do anything
    if (likeClicked) return;

    hapticsImpactLight();
    setLikeClicked(true);
    setDislikeClicked(false);

    // Trigger animation
    setLikeAnimating(true);
    setTimeout(() => setLikeAnimating(false), 300);

    try {
      // Only use WebSocket method for feedback
      likeMessage(true, checkpointId ?? null, threadId ?? null);
    } catch (error) {
      console.error("Error liking chatbot response:", error);
    }
  };

  const handleDislikeClick = async () => {
    // If already disliked, don't do anything
    if (dislikeClicked) return;

    hapticsImpactLight();
    setDislikeClicked(true);
    setLikeClicked(false);

    // Trigger animation
    setDislikeAnimating(true);
    setTimeout(() => setDislikeAnimating(false), 300);

    try {
      // Only use WebSocket method for feedback
      likeMessage(false, checkpointId ?? null, threadId ?? null);
    } catch (error) {
      console.error("Error disliking chatbot response:", error);
    }
  };

  return (
    <div className="px-[20px]">
      <div className="mt-[10px]">
        <div className="pt-[20px]">
          <span className="text-[20px] text-white font-semibold">
            {userQuestion}
          </span>

          {/* <div className="flex mt-7 gap-[12px] items-center">
            <img src={chatAiLoading} alt="chatAiLoading" />
            <span className="text-[20px] text-white font-semibold">
              {"RotoBot's Response"}
            </span>
          </div> */}

          {chartData && Object.keys(chartData).length > 0 && (
            <ChartRender chartResponse={chartData} />
          )}
          <div className="flex-col space-y-[7px] mt-[10px]">
            <article className="prose prose-slate prose-table:overflow-hidden max-w-none w-full ">
              <Markdown
                options={{
                  overrides: {
                    ...markdownStyleOverrides,
                    strong: {
                      component: ClickableBold,
                    },
                    table: {
                      component: ({ children, ...props }) => (
                        <InteractiveTable {...props}>
                          {children}
                        </InteractiveTable>
                      ),
                    },
                    td: {
                      component: ({ children, ...props }) => (
                        <td {...props}>
                          <span className="table-data-span">{children}</span>
                        </td>
                      ),
                    },
                  },
                }}
              >
                {botResponse}
              </Markdown>
            </article>
          </div>
        </div>

        {!modalDatathreadId && (
          <div className="mt-8 flex justify-center items-center gap-[15px]">
            <div>
              <span className="text-white text-[13px] font-semibold">
                Did you like this response?
              </span>
            </div>
            <div className="flex items-center gap-2.5">
              <div
                className={`w-[24.96px] h-[24.96px] cursor-pointer rounded-[6px] bg-white/10 flex items-center justify-center transition-all duration-300 ${
                  likeClicked ? "bg-white/20" : ""
                }`}
                onClick={handleLikeClick}
              >
                <img
                  src={likeClicked ? like : likeInactive}
                  alt="like"
                  className={`transition-all ${likeClicked ? "w-[14.73px] h-[13.48px]" : "w-[13.48px] h-[12.23px]"}  duration-300 ${
                    likeAnimating
                      ? "scale-90 rotate-[10deg]"
                      : "hover:scale-105 hover:rotate-[-5deg]"
                  } `}
                />
              </div>
              <div
                className={`w-[24.96px] h-[24.96px] cursor-pointer rounded-[6px] bg-white/10 flex items-center justify-center transition-all duration-300 ${
                  dislikeClicked ? "bg-white/20" : ""
                }`}
                onClick={handleDislikeClick}
              >
                <img
                  src={dislikeClicked ? dislike : dislikeInactive}
                  alt="dislike"
                  className={`transition-all ${dislikeClicked ? "w-[14.73px] h-[13.48px]" : "w-[13.48px] h-[12.23px]"} duration-300 ${
                    dislikeAnimating
                      ? "scale-90 rotate-[-10deg]"
                      : "hover:scale-105 hover:rotate-[5deg]"
                  } `}
                />
              </div>
            </div>
          </div>
        )}

        {relatedQuestions.length > 0 && (
          <div className="pt-[25px] ">
            <div className="flex mt-5 gap-[10px]">
              <img
                src={followUpQuestions}
                alt="followUpQuestions"
                className="w-[18.72px] h-[17px]"
              />
              <span className="text-[13px] text-white font-semibold">
                Follow Up Questions
              </span>
            </div>

            <div className="flex-col space-y-[7px] mt-[10px]">
              {relatedQuestions.map((question, index) => {
                const isLoading =
                  showSkeleton ||
                  messageLoading ||
                  typingLoading ||
                  followUpQuestionsLoading;
                return (
                  <div
                    onClick={
                      isLoading ? undefined : () => onQuestionClick?.(question)
                    }
                    key={index}
                    className={`flex justify-between gap-3 items-center border-b border-white/10 py-3 pr-1`}
                  >
                    <span className="text-white/70 text-[13px] font-medium">
                      {question}
                    </span>
                    <img src={followUp} alt="rightArrow" />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatBotHistory;
